import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Info, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { EnergyConversionResponse, EnergyPackage, GameProfileResponse } from '@lunit/shared-types';
import { gameApi } from '@core/game/api';
import { useUser } from '@core/contexts/ProfileContext';
import { EnergyPackageCard } from '@core/game/components/EnergyPackageCard';
import { BalanceCard } from '@core/game/components/BalanceCard';

const ENERGY_PACKAGES: EnergyPackage[] = [
  {
    energy: 100,
    price: 500,
    bonus: 0
  },
  {
    energy: 500,
    price: 2000,
    bonus: 10,
    popular: true
  },
  {
    energy: 1000,
    price: 3500,
    bonus: 15
  },
  {
    energy: 5000,
    price: 15000,
    bonus: 25
  }
];

export const EnergyShopPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [purchasingIndex, setPurchasingIndex] = useState<number | null>(null);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['gameProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not found');
      }
      return gameApi.getProfile(user.id) as Promise<GameProfileResponse>;
    },
    enabled: Boolean(user?.id),
    refetchInterval: 10000
  });

  const profile = profileData?.profile;
  const currentCoins = Number(profile?.coins ?? 0);

  const convertMutation = useMutation({
    mutationFn: (coinsToSpend: number) => {
      if (!user?.id) {
        throw new Error('User not found');
      }
      return gameApi.convertEnergy(user.id, coinsToSpend);
    },
    onSuccess: (response: EnergyConversionResponse) => {
      toast.success(`🎉 Bought ${response.data?.energyGained ?? 0} energy!`, {
        position: 'top-center'
      });
      void queryClient.invalidateQueries({ queryKey: ['gameProfile'] });
      setPurchasingIndex(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to purchase energy', {
        position: 'top-center'
      });
      setPurchasingIndex(null);
    }
  });

  const handlePurchase = (pkg: EnergyPackage, index: number) => {
    if (!profile || convertMutation.isPending) {
      return;
    }

    if (currentCoins < pkg.price) {
      toast.error('Not enough coins!', { icon: '💰' });
      return;
    }

    setPurchasingIndex(index);
    convertMutation.mutate(pkg.price);
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary text-white">
        Please sign in to access the shop.
      </div>
    );
  }

  if (isLoading || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-dark">
        <span className="h-12 w-12 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark text-white">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-primary/80 backdrop-blur-lg p-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/game')}
            className="rounded-full p-2 transition-colors hover:bg-white/10"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Energy Shop</h1>
          </div>
        </div>
      </header>

      <main className="space-y-6 p-4 pb-16">
        <BalanceCard coins={profile.coins} energy={profile.energy} maxEnergy={100} />

        <section className="rounded-2xl border border-primary/20 bg-primary/10 p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/30">
              <Info className="h-5 w-5 text-white" />
            </div>
            <div className="text-sm text-white">
              <p className="font-semibold">How it works</p>
              <p className="opacity-80">
                Energy powers your coffee machine. It regenerates over time, but you can buy extra
                using coins. Bigger bundles give bonus energy!
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Energy Packages</h2>

          {ENERGY_PACKAGES.map((pkg, index) => {
            const canAfford = currentCoins >= pkg.price;
            const isLoadingPackage = purchasingIndex === index && convertMutation.isPending;

            return (
              <EnergyPackageCard
                key={`${pkg.energy}-${pkg.price}`}
                package={pkg}
                onPurchase={() => handlePurchase(pkg, index)}
                disabled={!canAfford}
                loading={isLoadingPackage}
              />
            );
          })}
        </section>

        <div className="h-12" />
      </main>
    </div>
  );
};

